import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Lock, Eye, EyeOff, Smartphone, Music, Users, Mail, 
  MessageSquare, Settings, LogOut, Plus, Edit, Trash2,
  ChevronRight, Check, X, ExternalLink, RefreshCw,
  BarChart3, Link as LinkIcon, Shield, Key, Download,
  Loader2, CheckCircle, AlertCircle, FileText, Disc3,
  Copy, Play
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const API = "/api";

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('reset');
  
  const [step, setStep] = useState(resetToken ? "reset" : 1); // 1: password, 2: totp, 3: setup, "forgot", "reset"
  const [email, setEmail] = useState("cf@cfaudio.fr");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      setTempToken(response.data.temp_token);
      setStep(2);
      toast.success("Entrez le code Authenticator");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Identifiants invalides");
      } else {
        // Try setup
        try {
          const setupRes = await axios.post(`${API}/admin/setup`);
          setQrCode(setupRes.data.qr_code);
          setManualKey(setupRes.data.manual_key);
          setStep(3);
          toast.success("Scannez le QR code avec Google Authenticator");
        } catch (setupErr) {
          toast.error("Erreur de connexion");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Entrez votre email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`, { email });
      toast.success("Si cet email existe, un lien de réinitialisation a été envoyé.");
      setStep(1);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success("Mot de passe réinitialisé ! Vous pouvez vous connecter.");
      window.location.href = "/admin";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lien invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/verify-totp`, {
        email,
        totp_code: totpCode,
        temp_token: tempToken
      });
      sessionStorage.setItem("admin_token", response.data.access_token);
      toast.success("Connexion réussie !");
      onLogin(response.data.access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  // Reset password screen
  if (step === "reset") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#0A0908]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#FFB347]/10 flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-[#FFB347]" />
              </div>
              <h1 className="font-display text-2xl font-bold">Nouveau mot de passe</h1>
              <p className="text-[#A8A29E] mt-2">Choisissez un nouveau mot de passe sécurisé</p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Confirmer le mot de passe</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? "..." : "Réinitialiser"}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Forgot password screen
  if (step === "forgot") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#0A0908]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#FFB347]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#FFB347]" />
              </div>
              <h1 className="font-display text-2xl font-bold">Mot de passe oublié</h1>
              <p className="text-[#A8A29E] mt-2">Entrez votre email pour recevoir un lien de réinitialisation</p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? "..." : "Envoyer le lien"}
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-sm text-[#A8A29E] hover:text-[#FFB347]"
              >
                Retour à la connexion
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Setup screen - show QR code
  if (step === 3) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#0A0908]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.3)] p-8"
        >
          <div className="text-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_78551dcc-3f96-482d-9a37-f271494e00a9/artifacts/lb374gtc_Image%20PNG-475F-90F1-76-0.png" 
              alt="Sapito-Records" 
              className="w-16 h-16 mx-auto mb-4"
            />
            <h2 className="font-display text-2xl font-bold">Configuration</h2>
            <p className="text-[#A8A29E] mt-2">
              Scannez ce QR code avec Google Authenticator ou Authy
            </p>
          </div>

          <div className="bg-white p-4 rounded-xl mb-6">
            <img src={qrCode} alt="QR Code" className="w-full" />
          </div>

          <div className="mb-6">
            <p className="text-sm text-[#A8A29E] mb-2">Ou entrez cette clé manuellement :</p>
            <div className="bg-[#0A0908] p-3 rounded-lg font-mono text-sm text-[#FFB347] break-all select-all">
              {manualKey}
            </div>
          </div>

          <button
            onClick={() => setStep(1)}
            className="btn-primary w-full"
          >
            J'ai scanné, me connecter
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0A0908]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.3)] p-8"
      >
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_78551dcc-3f96-482d-9a37-f271494e00a9/artifacts/lb374gtc_Image%20PNG-475F-90F1-76-0.png" 
            alt="Sapito-Records" 
            className="w-16 h-16 mx-auto mb-4"
          />
          <h1 className="font-display text-2xl font-bold">Administration</h1>
          <p className="text-[#A8A29E] mt-2">
            {step === 1 ? "Connectez-vous" : "Code Google Authenticator"}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-[#A8A29E] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A8A29E] mb-2">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-12"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#FFB347]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="admin-login-btn"
            >
              {loading ? "Vérification..." : "Continuer"}
            </button>
            <button
              type="button"
              onClick={() => setStep("forgot")}
              className="w-full text-center text-sm text-[#A8A29E] hover:text-[#FFB347] mt-2"
            >
              Mot de passe oublié ?
            </button>
          </form>
        ) : (
          <form onSubmit={handleTotpSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-[#A8A29E] mb-2">Code à 6 chiffres</label>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input-field text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
                data-testid="admin-totp-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || totpCode.length !== 6}
              className="btn-primary w-full"
              data-testid="admin-verify-btn"
            >
              {loading ? "Vérification..." : "Vérifier"}
            </button>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-[#A8A29E] hover:text-[#FFB347] text-sm"
            >
              ← Retour
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// Dashboard Component
const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [sources, setSources] = useState([]);
  const [siteContent, setSiteContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [demos, setDemos] = useState([]);

  // Headers must be recreated on each request to use current token
  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const headers = getHeaders();
      const [dashRes, tracksRes, artistsRes, sourcesRes, demosRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/tracks`),
        axios.get(`${API}/artists`),
        axios.get(`${API}/admin/sources`, { headers }),
        axios.get(`${API}/admin/demos`, { headers })
      ]);
      setStats(dashRes.data.stats);
      setContacts(dashRes.data.recent_contacts || []);
      setSubscribers(dashRes.data.recent_subscribers || []);
      setTracks(tracksRes.data.tracks || tracksRes.data);
      setArtists(artistsRes.data.artists || artistsRes.data);
      setSources(sourcesRes.data);
      setDemos(demosRes.data);
      
      // Load site content
      const contentRes = await axios.get(`${API}/admin/content`, { headers });
      setSiteContent(contentRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        onLogout();
      }
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const headers = getHeaders();
      const res = await axios.get(`${API}/admin/contacts`, { headers });
      setContacts(res.data);
    } catch (error) {
      toast.error("Erreur de chargement des contacts");
    }
  };

  const fetchSiteContent = async () => {
    try {
      const headers = getHeaders();
      const res = await axios.get(`${API}/admin/content`, { headers });
      setSiteContent(res.data);
    } catch (error) {
      toast.error("Erreur de chargement du contenu");
    }
  };

  const fetchSubscribers = async () => {
    try {
      const headers = getHeaders();
      const res = await axios.get(`${API}/admin/newsletter`, { headers });
      setSubscribers(res.data);
    } catch (error) {
      toast.error("Erreur de chargement des abonnés");
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = async (type, id) => {
    try {
      const headers = getHeaders();
      await axios.delete(`${API}/admin/${type}/${id}`, { headers });
      toast.success("Supprimé avec succès");
      setDeleteConfirm(null);
      
      if (type === "tracks") {
        const tracksRes = await axios.get(`${API}/tracks`);
        setTracks(tracksRes.data.tracks || tracksRes.data);
      } else if (type === "artists") {
        const artistsRes = await axios.get(`${API}/artists`);
        setArtists(artistsRes.data.artists || artistsRes.data);
      } else {
        fetchDashboard();
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
  };

  const tabs = [
    { id: "dashboard", label: "Tableau de bord", icon: BarChart3 },
    { id: "import", label: "Import Auto", icon: Download },
    { id: "tracks", label: "Tracks", icon: Music },
    { id: "artists", label: "Artistes", icon: Users },
    { id: "demos", label: "Démos", icon: Disc3 },
    { id: "content", label: "Contenu Site", icon: FileText },
    { id: "sources", label: "Sources", icon: LinkIcon },
    { id: "contacts", label: "Messages", icon: MessageSquare },
    { id: "newsletter", label: "Newsletter", icon: Mail },
    { id: "settings", label: "Paramètres", icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0908]">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFB347] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0908]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#141210] border-r border-[rgba(139,69,19,0.2)] p-4 z-40">
        <div className="flex items-center gap-3 mb-8 px-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_78551dcc-3f96-482d-9a37-f271494e00a9/artifacts/lb374gtc_Image%20PNG-475F-90F1-76-0.png" 
            alt="Sapito-Records" 
            className="w-10 h-10"
          />
          <div>
            <p className="font-display font-bold">Sapito-Records</p>
            <p className="text-xs text-[#A8A29E]">Administration</p>
          </div>
        </div>

        <nav className="space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "contacts") fetchContacts();
                if (tab.id === "newsletter") fetchSubscribers();
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === tab.id
                  ? "bg-[#FFB347]/20 text-[#FFB347]"
                  : "text-[#A8A29E] hover:bg-[#FFB347]/10 hover:text-[#F5F5F0]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>

        <button
          onClick={onLogout}
          className="absolute bottom-4 left-4 right-4 flex items-center gap-3 px-4 py-3 rounded-lg text-[#A8A29E] hover:bg-red-500/10 hover:text-red-400 transition-all"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Déconnexion
        </button>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div>
            <h1 className="font-display text-3xl font-bold mb-8">Tableau de bord</h1>
            
            <div className="grid grid-cols-4 gap-6 mb-8">
              {[
                { label: "Tracks", value: stats?.tracks || 0, icon: Music },
                { label: "Artistes", value: stats?.artists || 0, icon: Users },
                { label: "Messages", value: stats?.contacts || 0, icon: MessageSquare, badge: stats?.unread_contacts },
                { label: "Newsletter", value: stats?.newsletter_subscribers || 0, icon: Mail },
              ].map((stat, i) => (
                <div key={i} className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className="w-6 h-6 text-[#FFB347]" />
                    {stat.badge > 0 && (
                      <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                        {stat.badge} non lu{stat.badge > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <p className="font-display text-3xl font-bold">{stat.value}</p>
                  <p className="text-[#A8A29E] text-sm">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
                <h3 className="font-display text-lg font-semibold mb-4">Derniers messages</h3>
                {contacts.length === 0 ? (
                  <p className="text-[#A8A29E]">Aucun message</p>
                ) : (
                  <div className="space-y-3">
                    {contacts.slice(0, 5).map((contact) => (
                      <div key={contact.id} className="flex items-center justify-between p-3 bg-[#0A0908] rounded-lg">
                        <div>
                          <p className="font-medium">{contact.name}</p>
                          <p className="text-sm text-[#A8A29E]">{contact.subject}</p>
                        </div>
                        {!contact.read && (
                          <span className="w-2 h-2 bg-[#FFB347] rounded-full" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
                <h3 className="font-display text-lg font-semibold mb-4">Sources d'import</h3>
                <div className="space-y-3">
                  {sources.map((source) => (
                    <div key={source.id} className="flex items-center justify-between p-3 bg-[#0A0908] rounded-lg">
                      <div className="flex items-center gap-3">
                        <LinkIcon className="w-5 h-5 text-[#A8A29E]" />
                        <span>{source.name}</span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        source.enabled && source.url ? "bg-green-500/20 text-green-400" : "bg-[#A8A29E]/20 text-[#A8A29E]"
                      }`}>
                        {source.enabled && source.url ? "Actif" : "Inactif"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Tab */}
        {activeTab === "import" && (
          <ImportSection getHeaders={getHeaders} sources={sources} onImportComplete={fetchDashboard} />
        )}

        {/* Tracks Tab */}
        {activeTab === "tracks" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display text-3xl font-bold">Gestion des Tracks</h1>
              <button
                onClick={() => openModal("track")}
                className="btn-primary flex items-center gap-2"
                data-testid="add-track-btn"
              >
                <Plus className="w-5 h-5" />
                Ajouter une track
              </button>
            </div>

            <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#0A0908]">
                  <tr>
                    <th className="text-left p-4 text-[#A8A29E] font-medium">Titre</th>
                    <th className="text-left p-4 text-[#A8A29E] font-medium">Artiste</th>
                    <th className="text-left p-4 text-[#A8A29E] font-medium">Genre</th>
                    <th className="text-left p-4 text-[#A8A29E] font-medium">Featured</th>
                    <th className="text-right p-4 text-[#A8A29E] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((track) => (
                    <tr key={track.id} className="border-t border-[rgba(139,69,19,0.1)]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={track.cover_image || "https://via.placeholder.com/40"}
                            alt={track.title}
                            className="w-10 h-10 rounded object-cover"
                          />
                          <span className="font-medium">{track.title}</span>
                        </div>
                      </td>
                      <td className="p-4 text-[#A8A29E]">{track.artist}</td>
                      <td className="p-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-[#8B4513]/20 text-[#FFB347]">
                          {track.genre}
                        </span>
                      </td>
                      <td className="p-4">
                        {track.featured ? (
                          <Check className="w-5 h-5 text-green-400" />
                        ) : (
                          <X className="w-5 h-5 text-[#A8A29E]" />
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openModal("track", track)}
                            className="p-2 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
                            data-testid={`edit-track-${track.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (deleteConfirm === track.id) {
                                handleDelete("tracks", track.id);
                              } else {
                                setDeleteConfirm(track.id);
                                setTimeout(() => setDeleteConfirm(null), 3000);
                              }
                            }}
                            className={`p-2 rounded transition-colors ${
                              deleteConfirm === track.id 
                                ? 'bg-red-500 text-white' 
                                : 'text-[#A8A29E] hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            data-testid={`delete-track-${track.id}`}
                            title={deleteConfirm === track.id ? "Cliquer pour confirmer" : "Supprimer"}
                          >
                            {deleteConfirm === track.id ? (
                              <span className="text-xs font-bold px-1">CONFIRMER</span>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Artists Tab */}
        {activeTab === "artists" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display text-3xl font-bold">Gestion des Artistes</h1>
              <button
                onClick={() => openModal("artist")}
                className="btn-primary flex items-center gap-2"
                data-testid="add-artist-btn"
              >
                <Plus className="w-5 h-5" />
                Ajouter un artiste
              </button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {artists.map((artist) => (
                <div key={artist.id} className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] overflow-hidden">
                  <img
                    src={artist.image || "https://via.placeholder.com/300"}
                    alt={artist.name}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-display font-semibold text-lg">{artist.name}</h3>
                    <p className="text-[#A8A29E] text-sm line-clamp-2 mt-1">{artist.bio}</p>
                    <div className="flex items-center justify-end gap-2 mt-4">
                      <button
                        onClick={() => openModal("artist", artist)}
                        className="p-2 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (deleteConfirm === artist.id) {
                            handleDelete("artists", artist.id);
                          } else {
                            setDeleteConfirm(artist.id);
                            setTimeout(() => setDeleteConfirm(null), 3000);
                          }
                        }}
                        className={`p-2 rounded transition-colors ${
                          deleteConfirm === artist.id 
                            ? 'bg-red-500 text-white' 
                            : 'text-[#A8A29E] hover:text-red-400 hover:bg-red-400/10'
                        }`}
                        title={deleteConfirm === artist.id ? "Cliquer pour confirmer" : "Supprimer"}
                      >
                        {deleteConfirm === artist.id ? (
                          <span className="text-xs font-bold px-1">CONFIRMER</span>
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === "content" && (
          <ContentManager getHeaders={getHeaders} content={siteContent} onUpdate={fetchSiteContent} />
        )}

        {/* Demos Tab */}
        {activeTab === "demos" && (
          <DemosManager getHeaders={getHeaders} demos={demos} onUpdate={fetchDashboard} />
        )}

        {/* Sources Tab */}
        {activeTab === "sources" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-3xl font-bold">Sources d'import</h1>
                <p className="text-[#A8A29E] mt-1">
                  Configurez vos plateformes de diffusion pour importer vos tracks
                </p>
              </div>
              <button
                onClick={() => openModal("source")}
                className="btn-primary flex items-center gap-2"
                data-testid="add-source-btn"
              >
                <Plus className="w-5 h-5" />
                Ajouter une source
              </button>
            </div>

            <div className="space-y-4">
              {sources.map((source) => (
                <div
                  key={source.id}
                  className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        source.enabled && source.url ? "bg-[#FFB347]/20" : "bg-[#A8A29E]/10"
                      }`}>
                        <LinkIcon className={`w-6 h-6 ${
                          source.enabled && source.url ? "text-[#FFB347]" : "text-[#A8A29E]"
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-lg">{source.name}</h3>
                        {source.url ? (
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#A8A29E] hover:text-[#FFB347] flex items-center gap-1"
                          >
                            {source.url.substring(0, 50)}...
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <p className="text-sm text-[#A8A29E]">Aucune URL configurée</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 text-sm rounded-full ${
                        source.enabled && source.url
                          ? "bg-green-500/20 text-green-400"
                          : "bg-[#A8A29E]/20 text-[#A8A29E]"
                      }`}>
                        {source.enabled && source.url ? "Actif" : "Inactif"}
                      </span>
                      <button
                        onClick={() => openModal("source", source)}
                        className="p-2 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete("sources", source.id)}
                        className="p-2 text-[#A8A29E] hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-[#FFB347]/10 rounded-xl border border-[#FFB347]/30">
              <h4 className="font-display font-semibold mb-2 text-[#FFB347]">
                💡 Comment ça marche ?
              </h4>
              <p className="text-[#A8A29E] text-sm">
                Ajoutez l'URL de votre profil artiste sur chaque plateforme (SoundCloud, Spotify, Apple Music, Toolost...).
                Vous pourrez modifier ces adresses à tout moment si vous changez de diffuseur.
              </p>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div>
            <h1 className="font-display text-3xl font-bold mb-8">Messages reçus</h1>

            {contacts.length === 0 ? (
              <div className="text-center py-16 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)]">
                <MessageSquare className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#A8A29E]">Aucun message reçu</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className={`bg-[#141210] rounded-xl border p-6 ${
                      contact.read ? "border-[rgba(139,69,19,0.2)]" : "border-[#FFB347]/50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-display font-semibold text-lg">{contact.subject}</h3>
                        <p className="text-[#A8A29E] text-sm">
                          De: {contact.name} ({contact.email})
                        </p>
                        {contact.demo_type && (
                          <span className="inline-block mt-2 px-2 py-1 text-xs rounded-full bg-[#FFB347]/20 text-[#FFB347]">
                            Démo: {contact.demo_type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!contact.read && (
                          <button
                            onClick={async () => {
                              const headers = getHeaders();
                              await axios.put(`${API}/admin/contacts/${contact.id}/read`, {}, { headers });
                              fetchContacts();
                            }}
                            className="p-2 text-[#A8A29E] hover:text-green-400 transition-colors"
                            title="Marquer comme lu"
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete("contacts", contact.id)}
                          className="p-2 text-[#A8A29E] hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[#F5F5F0] whitespace-pre-wrap">{contact.message}</p>
                    {contact.soundcloud_link && (
                      <a
                        href={contact.soundcloud_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 text-[#FFB347] hover:underline"
                      >
                        <Music className="w-4 h-4" />
                        Écouter la démo
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === "newsletter" && (
          <div>
            <h1 className="font-display text-3xl font-bold mb-8">Abonnés Newsletter</h1>

            {subscribers.length === 0 ? (
              <div className="text-center py-16 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)]">
                <Mail className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#A8A29E]">Aucun abonné</p>
              </div>
            ) : (
              <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#0A0908]">
                    <tr>
                      <th className="text-left p-4 text-[#A8A29E] font-medium">Email</th>
                      <th className="text-left p-4 text-[#A8A29E] font-medium">Date d'inscription</th>
                      <th className="text-right p-4 text-[#A8A29E] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub) => (
                      <tr key={sub.id} className="border-t border-[rgba(139,69,19,0.1)]">
                        <td className="p-4">{sub.email}</td>
                        <td className="p-4 text-[#A8A29E]">
                          {new Date(sub.subscribed_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleDelete("newsletter", sub.id)}
                            className="p-2 text-[#A8A29E] hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div>
            <h1 className="font-display text-3xl font-bold mb-8">Paramètres</h1>

            <div className="space-y-6">
              <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
                <div className="flex items-center gap-4 mb-4">
                  <Key className="w-6 h-6 text-[#FFB347]" />
                  <h3 className="font-display font-semibold text-lg">Changer le mot de passe</h3>
                </div>
                <ChangePasswordForm getHeaders={getHeaders} />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <EditModal
          type={modalType}
          item={editingItem}
          getHeaders={getHeaders}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingItem(null);
            fetchDashboard();
          }}
        />
      )}
    </div>
  );
};

// Content Manager Component
const ContentManager = ({ getHeaders, content, onUpdate }) => {
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [selectedLang, setSelectedLang] = useState("fr");
  const [saving, setSaving] = useState(false);

  const contentKeys = [
    { key: "about_title", label: "À propos - Titre", type: "text" },
    { key: "about_text", label: "À propos - Texte", type: "textarea" },
    { key: "hero_title", label: "Accueil - Titre principal", type: "text" },
    { key: "hero_subtitle", label: "Accueil - Sous-titre", type: "textarea" },
    { key: "promo_banner", label: "Bannière promo", type: "textarea" },
    { key: "promo_active", label: "Bannière active (true/false)", type: "text" },
    { key: "featured_title", label: "Sorties en vedette - Titre", type: "text" },
    { key: "featured_text", label: "Sorties en vedette - Description", type: "textarea" },
  ];

  const languages = [
    { code: "fr", name: "Français" },
    { code: "en", name: "English" },
    { code: "es", name: "Español" },
    { code: "de", name: "Deutsch" },
    { code: "it", name: "Italiano" },
    { code: "pt", name: "Português" },
    { code: "ja", name: "日本語" },
    { code: "zh", name: "中文" },
    { code: "ar", name: "العربية" },
    { code: "ru", name: "Русский" },
  ];

  const getContentValue = (key, lang) => {
    const item = content.find(c => c.key === key && c.lang === lang);
    return item?.value || "";
  };

  const handleSave = async (key, value) => {
    setSaving(true);
    try {
      const headers = getHeaders();
      await axios.post(`${API}/admin/content?key=${key}&value=${encodeURIComponent(value)}&lang=${selectedLang}`, {}, { headers });
      toast.success("Contenu sauvegardé");
      setEditingKey(null);
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleAddCustom = async () => {
    if (!newKey || !newValue) {
      toast.error("Remplissez la clé et la valeur");
      return;
    }
    await handleSave(newKey, newValue);
    setNewKey("");
    setNewValue("");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold">Contenu du Site</h1>
          <p className="text-[#A8A29E] mt-1">
            Gérez les textes, bannières et messages promotionnels
          </p>
        </div>
        <select
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
          className="bg-[#141210] border border-[rgba(139,69,19,0.2)] rounded-lg px-4 py-2 text-[#F5F5F0]"
        >
          {languages.map(lang => (
            <option key={lang.code} value={lang.code}>{lang.name}</option>
          ))}
        </select>
      </div>

      <div className="space-y-4">
        {contentKeys.map(({ key, label, type }) => {
          const currentValue = getContentValue(key, selectedLang);
          const isEditing = editingKey === key;

          return (
            <div key={key} className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <label className="text-[#FFB347] text-sm font-medium">{label}</label>
                  {isEditing ? (
                    <div className="mt-2">
                      {type === "textarea" ? (
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          rows={4}
                          className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                        />
                      ) : (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
                        />
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleSave(key, editValue)}
                          disabled={saving}
                          className="btn-primary text-sm"
                        >
                          {saving ? "..." : "Sauvegarder"}
                        </button>
                        <button
                          onClick={() => setEditingKey(null)}
                          className="px-4 py-2 text-[#A8A29E] hover:text-[#F5F5F0] transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-[#F5F5F0] whitespace-pre-wrap">
                      {currentValue || <span className="text-[#A8A29E] italic">Non défini</span>}
                    </p>
                  )}
                </div>
                {!isEditing && (
                  <button
                    onClick={() => {
                      setEditingKey(key);
                      setEditValue(currentValue);
                    }}
                    className="p-2 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Custom content */}
        <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6 mt-8">
          <h3 className="font-display font-semibold text-lg mb-4">Ajouter un contenu personnalisé</h3>
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Clé (ex: promo_summer)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
            />
            <input
              type="text"
              placeholder="Valeur"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className="bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-4 py-3 text-[#F5F5F0] focus:border-[#FFB347] focus:outline-none"
            />
          </div>
          <button
            onClick={handleAddCustom}
            className="btn-primary mt-4"
          >
            Ajouter
          </button>
        </div>

        {/* Existing custom content */}
        {content.filter(c => !contentKeys.find(ck => ck.key === c.key) && c.lang === selectedLang).length > 0 && (
          <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
            <h3 className="font-display font-semibold text-lg mb-4">Contenus personnalisés</h3>
            <div className="space-y-3">
              {content.filter(c => !contentKeys.find(ck => ck.key === c.key) && c.lang === selectedLang).map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-[#0A0908] rounded-lg">
                  <div>
                    <span className="text-[#FFB347] text-sm">{c.key}</span>
                    <p className="text-[#F5F5F0]">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Demos Manager Component
const DemosManager = ({ getHeaders, demos, onUpdate }) => {
  const [filter, setFilter] = useState("all");

  const statusLabels = {
    nouveau: { label: "Nouveau", color: "bg-blue-500" },
    en_cours: { label: "En cours", color: "bg-yellow-500" },
    repondu: { label: "Répondu", color: "bg-green-500" },
    refuse: { label: "Refusé", color: "bg-red-500" },
    valide: { label: "Validé", color: "bg-emerald-500" },
    supprime: { label: "Supprimé", color: "bg-gray-500" }
  };

  const filteredDemos = filter === "all" 
    ? demos.filter(d => d.status !== "supprime")
    : demos.filter(d => d.status === filter);

  const updateStatus = async (demoId, newStatus) => {
    try {
      const headers = getHeaders();
      await axios.put(`${API}/admin/demos/${demoId}/status`, { status: newStatus }, { headers });
      toast.success("Statut mis à jour");
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteDemo = async (demoId) => {
    if (!window.confirm("Supprimer cette démo ?")) return;
    try {
      const headers = getHeaders();
      await axios.delete(`${API}/admin/demos/${demoId}`, { headers });
      toast.success("Démo supprimée");
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold">Démos reçues</h1>
          <p className="text-[#A8A29E] mt-1">
            {filteredDemos.length} démo{filteredDemos.length > 1 ? 's' : ''}
          </p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-[#141210] border border-[rgba(139,69,19,0.2)] rounded-lg px-4 py-2 text-[#F5F5F0]"
        >
          <option value="all">Toutes</option>
          <option value="nouveau">Nouveaux</option>
          <option value="en_cours">En cours</option>
          <option value="repondu">Répondus</option>
          <option value="valide">Validés</option>
          <option value="refuse">Refusés</option>
        </select>
      </div>

      {filteredDemos.length === 0 ? (
        <div className="text-center py-12 text-[#A8A29E]">
          Aucune démo {filter !== "all" ? "avec ce statut" : "reçue"}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDemos.map((demo) => (
            <div
              key={demo.id}
              className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-display font-semibold text-lg">{demo.artist_name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs text-white ${statusLabels[demo.status]?.color || 'bg-gray-500'}`}>
                      {statusLabels[demo.status]?.label || demo.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-[#A8A29E] mb-3">
                    <button
                      onClick={() => copyToClipboard(demo.email, "Email")}
                      className="flex items-center gap-1 hover:text-[#FFB347] transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {demo.email}
                      <Copy className="w-3 h-3" />
                    </button>
                    <span className="text-[#8B4513]">•</span>
                    <span>{new Date(demo.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <a
                      href={demo.demo_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#FFB347] hover:underline"
                    >
                      <Play className="w-4 h-4" />
                      Écouter la démo
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <button
                      onClick={() => copyToClipboard(demo.demo_link, "Lien")}
                      className="p-1 text-[#A8A29E] hover:text-[#FFB347] transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  {demo.message && (
                    <p className="text-[#A8A29E] text-sm bg-[#0A0908] p-3 rounded-lg">
                      {demo.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <select
                    value={demo.status}
                    onChange={(e) => updateStatus(demo.id, e.target.value)}
                    className="bg-[#0A0908] border border-[rgba(139,69,19,0.3)] rounded-lg px-3 py-2 text-sm text-[#F5F5F0]"
                  >
                    <option value="nouveau">Nouveau</option>
                    <option value="en_cours">En cours</option>
                    <option value="repondu">Répondu</option>
                    <option value="valide">Validé</option>
                    <option value="refuse">Refusé</option>
                  </select>
                  <button
                    onClick={() => deleteDemo(demo.id)}
                    className="p-2 text-[#A8A29E] hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Import Section Component
const ImportSection = ({ getHeaders, sources, onImportComplete }) => {
  const [selectedSources, setSelectedSources] = useState([]);
  const [previewTracks, setPreviewTracks] = useState([]);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState(1); // 1: select sources, 2: preview, 3: done

  const activeSources = sources.filter(s => s.enabled && s.url);

  const toggleSource = (sourceId) => {
    setSelectedSources(prev => 
      prev.includes(sourceId) 
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const toggleAllSources = () => {
    if (selectedSources.length === activeSources.length) {
      setSelectedSources([]);
    } else {
      setSelectedSources(activeSources.map(s => s.id));
    }
  };

  const handlePreview = async () => {
    if (selectedSources.length === 0) {
      toast.error("Sélectionnez au moins une source");
      return;
    }

    setLoading(true);
    try {
      const headers = getHeaders();
      const response = await axios.post(`${API}/admin/import/preview`, selectedSources, { headers });
      setPreviewTracks(response.data.preview);
      setSelectedTracks(response.data.preview.map((_, i) => i));
      setStep(2);
      
      if (response.data.preview.length === 0) {
        toast.info("Aucune nouvelle track trouvée");
      } else {
        toast.success(`${response.data.tracks_found} track(s) trouvée(s)`);
      }
    } catch (error) {
      toast.error("Erreur lors de la prévisualisation");
    } finally {
      setLoading(false);
    }
  };

  const toggleTrack = (index) => {
    setSelectedTracks(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleAllTracks = () => {
    if (selectedTracks.length === previewTracks.length) {
      setSelectedTracks([]);
    } else {
      setSelectedTracks(previewTracks.map((_, i) => i));
    }
  };

  const handleImport = async () => {
    if (selectedTracks.length === 0) {
      toast.error("Sélectionnez au moins une track à importer");
      return;
    }

    setImporting(true);
    try {
      const headers = getHeaders();
      const tracksToImport = selectedTracks.map(i => previewTracks[i]);
      const response = await axios.post(`${API}/admin/import/execute`, tracksToImport, { headers });
      toast.success(response.data.message);
      setStep(3);
      onImportComplete();
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedSources([]);
    setPreviewTracks([]);
    setSelectedTracks([]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold">Import Automatique</h1>
          <p className="text-[#A8A29E] mt-1">
            Récupérez vos tracks depuis vos plateformes de diffusion
          </p>
        </div>
        {step > 1 && (
          <button onClick={reset} className="btn-secondary flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Recommencer
          </button>
        )}
      </div>

      {/* Step 1: Select Sources */}
      {step === 1 && (
        <div className="space-y-6">
          {activeSources.length === 0 ? (
            <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-8 text-center">
              <AlertCircle className="w-12 h-12 text-[#FFB347] mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold mb-2">Aucune source configurée</h3>
              <p className="text-[#A8A29E] mb-4">
                Configurez d'abord vos URLs de diffusion dans l'onglet "Sources"
              </p>
              <button
                onClick={() => document.querySelector('[data-testid="tab-sources"]')?.click()}
                className="btn-primary"
              >
                Configurer les sources
              </button>
            </div>
          ) : (
            <>
              <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-semibold">
                    Sélectionnez les sources à importer
                  </h3>
                  <button
                    onClick={toggleAllSources}
                    className="text-sm text-[#FFB347] hover:underline"
                  >
                    {selectedSources.length === activeSources.length ? "Tout désélectionner" : "Tout sélectionner"}
                  </button>
                </div>

                <div className="space-y-3">
                  {activeSources.map((source) => (
                    <label
                      key={source.id}
                      className={`flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-all ${
                        selectedSources.includes(source.id)
                          ? "bg-[#FFB347]/20 border border-[#FFB347]/50"
                          : "bg-[#0A0908] border border-transparent hover:border-[rgba(139,69,19,0.3)]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSources.includes(source.id)}
                        onChange={() => toggleSource(source.id)}
                        className="w-5 h-5 accent-[#FFB347]"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{source.name}</p>
                        <p className="text-sm text-[#A8A29E] truncate">{source.url}</p>
                      </div>
                      {source.last_sync && (
                        <span className="text-xs text-[#A8A29E]">
                          Dernier import: {new Date(source.last_sync).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={handlePreview}
                disabled={loading || selectedSources.length === 0}
                className="btn-primary flex items-center gap-2"
                data-testid="preview-import-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Prévisualiser l'import
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Step 2: Preview & Select Tracks */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">
                {previewTracks.length} track(s) trouvée(s)
              </h3>
              {previewTracks.length > 0 && (
                <button
                  onClick={toggleAllTracks}
                  className="text-sm text-[#FFB347] hover:underline"
                >
                  {selectedTracks.length === previewTracks.length ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
              )}
            </div>

            {previewTracks.length === 0 ? (
              <div className="text-center py-8">
                <Music className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#A8A29E]">Aucune nouvelle track à importer</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {previewTracks.map((track, index) => (
                  <label
                    key={index}
                    className={`flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-all ${
                      selectedTracks.includes(index)
                        ? "bg-[#FFB347]/20 border border-[#FFB347]/50"
                        : "bg-[#0A0908] border border-transparent hover:border-[rgba(139,69,19,0.3)]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTracks.includes(index)}
                      onChange={() => toggleTrack(index)}
                      className="w-5 h-5 accent-[#FFB347]"
                    />
                    {track.cover_image && (
                      <img
                        src={track.cover_image}
                        alt={track.title}
                        className="w-12 h-12 rounded object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{track.title}</p>
                      <p className="text-sm text-[#A8A29E]">{track.artist}</p>
                    </div>
                    <span className="px-2 py-1 text-xs rounded-full bg-[#8B4513]/20 text-[#FFB347]">
                      {track.source}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Retour
            </button>
            {previewTracks.length > 0 && (
              <button
                onClick={handleImport}
                disabled={importing || selectedTracks.length === 0}
                className="btn-primary flex items-center gap-2"
                data-testid="execute-import-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Importer {selectedTracks.length} track(s)
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Done */}
      {step === 3 && (
        <div className="bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="font-display text-2xl font-bold mb-2">Import terminé !</h3>
          <p className="text-[#A8A29E] mb-6">
            Vos tracks ont été ajoutées à votre catalogue
          </p>
          <div className="flex gap-4 justify-center">
            <button onClick={reset} className="btn-secondary">
              Nouvel import
            </button>
            <button
              onClick={() => document.querySelector('[data-testid="tab-tracks"]')?.click()}
              className="btn-primary"
            >
              Voir les tracks
            </button>
          </div>
        </div>
      )}

      {/* Help Box */}
      <div className="mt-8 p-6 bg-[#FFB347]/10 rounded-xl border border-[#FFB347]/30">
        <h4 className="font-display font-semibold mb-2 text-[#FFB347]">
          💡 Comment ça marche ?
        </h4>
        <ul className="text-[#A8A29E] text-sm space-y-2">
          <li>1. Sélectionnez les plateformes depuis lesquelles importer</li>
          <li>2. Prévisualisez les tracks trouvées</li>
          <li>3. Choisissez celles que vous voulez ajouter</li>
          <li>4. Les tracks déjà présentes seront ignorées automatiquement</li>
        </ul>
      </div>
    </div>
  );
};

// Change Password Form
const ChangePasswordForm = ({ getHeaders }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const headers = getHeaders();
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers });
      toast.success("Mot de passe modifié !");
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        placeholder="Mot de passe actuel"
        className="input-field"
        required
      />
      <input
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder="Nouveau mot de passe"
        className="input-field"
        required
      />
      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? "..." : "Modifier"}
      </button>
    </form>
  );
};

// Edit Modal
const EditModal = ({ type, item, getHeaders, onClose, onSave }) => {
  const [formData, setFormData] = useState(item || {});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = getHeaders();
      if (item?.id) {
        // Update
        await axios.put(`${API}/admin/${type}s/${item.id}`, formData, { headers });
        toast.success("Modifié avec succès");
      } else {
        // Create
        await axios.post(`${API}/admin/${type}s`, formData, { headers });
        toast.success("Créé avec succès");
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0908]/90 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.3)] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-[rgba(139,69,19,0.2)]">
          <h2 className="font-display text-xl font-bold">
            {item?.id ? "Modifier" : "Ajouter"} {type === "track" ? "une track" : type === "artist" ? "un artiste" : "une source"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {type === "track" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Titre *</label>
                  <input
                    type="text"
                    value={formData.title || ""}
                    onChange={(e) => handleChange("title", e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Artiste *</label>
                  <input
                    type="text"
                    value={formData.artist || ""}
                    onChange={(e) => handleChange("artist", e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Genre *</label>
                  <input
                    type="text"
                    value={formData.genre || ""}
                    onChange={(e) => handleChange("genre", e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Durée</label>
                  <input
                    type="text"
                    value={formData.duration || ""}
                    onChange={(e) => handleChange("duration", e.target.value)}
                    className="input-field"
                    placeholder="5:30"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#A8A29E] mb-1">Image de couverture (URL)</label>
                <input
                  type="url"
                  value={formData.cover_image || ""}
                  onChange={(e) => handleChange("cover_image", e.target.value)}
                  className="input-field"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">SoundCloud URL</label>
                  <input
                    type="url"
                    value={formData.soundcloud_url || ""}
                    onChange={(e) => handleChange("soundcloud_url", e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Spotify URL</label>
                  <input
                    type="url"
                    value={formData.spotify_url || ""}
                    onChange={(e) => handleChange("spotify_url", e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Apple Music URL</label>
                  <input
                    type="url"
                    value={formData.apple_music_url || ""}
                    onChange={(e) => handleChange("apple_music_url", e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Toolost URL</label>
                  <input
                    type="url"
                    value={formData.toolost_url || ""}
                    onChange={(e) => handleChange("toolost_url", e.target.value)}
                    className="input-field"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured || false}
                  onChange={(e) => handleChange("featured", e.target.checked)}
                  className="w-4 h-4 accent-[#FFB347]"
                />
                <label htmlFor="featured" className="text-sm">Mettre en vedette</label>
              </div>
            </>
          )}

          {type === "artist" && (
            <>
              <div>
                <label className="block text-sm text-[#A8A29E] mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#A8A29E] mb-1">Bio *</label>
                <textarea
                  value={formData.bio || ""}
                  onChange={(e) => handleChange("bio", e.target.value)}
                  className="input-field"
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#A8A29E] mb-1">Image (URL)</label>
                <input
                  type="url"
                  value={formData.image || ""}
                  onChange={(e) => handleChange("image", e.target.value)}
                  className="input-field"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">SoundCloud</label>
                  <input
                    type="url"
                    value={formData.soundcloud_url || ""}
                    onChange={(e) => handleChange("soundcloud_url", e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A8A29E] mb-1">Instagram</label>
                  <input
                    type="text"
                    value={formData.instagram || ""}
                    onChange={(e) => handleChange("instagram", e.target.value)}
                    className="input-field"
                    placeholder="@username"
                  />
                </div>
              </div>
            </>
          )}

          {type === "source" && (
            <>
              <div>
                <label className="block text-sm text-[#A8A29E] mb-1">Nom de la plateforme *</label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="input-field"
                  placeholder="SoundCloud, Spotify, Bandcamp..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#A8A29E] mb-1">URL du profil / playlist *</label>
                <input
                  type="url"
                  value={formData.url || ""}
                  onChange={(e) => handleChange("url", e.target.value)}
                  className="input-field"
                  placeholder="https://..."
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled !== false}
                  onChange={(e) => handleChange("enabled", e.target.checked)}
                  className="w-4 h-4 accent-[#FFB347]"
                />
                <label htmlFor="enabled" className="text-sm">Activer cette source</label>
              </div>
            </>
          )}

          <div className="flex justify-end gap-4 pt-4 border-t border-[rgba(139,69,19,0.2)]">
            <button type="button" onClick={onClose} className="btn-secondary">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? "..." : item?.id ? "Modifier" : "Créer"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Main Admin Page
const AdminPage = () => {
  // Use sessionStorage for non-persistent sessions (cleared when browser closes)
  const [token, setToken] = useState(sessionStorage.getItem("admin_token"));

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    sessionStorage.removeItem("admin_token");
    setToken(null);
    toast.info("Déconnecté");
  };

  if (!token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
